import React, { useMemo } from "react";
import {
  StyledChevronItem,
  StyledListContainer,
  StyledNumberPageItem,
} from "./Pagination.styled";
import { IconManager } from "../IconManager/IconManager";
import { FontWeightEnum, IconSizeEnum } from "../../../types/AppTypes";
import { ReactComponent as ChevronLeftIcon } from "../../../icons/chevronLeft.svg";
import { ReactComponent as ChevronRightIcon } from "../../../icons/chevronRight.svg";
import { BodyTyp } from "../../Typography/Typography";
import { theme } from "../../../constants/StyledComponents";
import { ReactComponent as SkipIcon } from "../../../icons/skip.svg";
import { ReactComponent as SkipBackIcon } from "../../../icons/skipBack.svg";

export interface PaginationProps {
  onChange: (page: number) => void;
  total: number;
  pageSize: number;
  current: number;
}

export const Pagination = ({
  total,
  pageSize,
  current,
  onChange,
}: PaginationProps) => {
  const siblingCount = 1;
  const DOTS = -1;

  const range = (start: number, end: number) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, idx) => idx + start);
  };

  const usePagination = (): number[] => {
    const paginationRange = useMemo(() => {
      const totalPageCount = Math.ceil(total / pageSize);
      const totalPageNumbers = siblingCount + 5;

      if (totalPageNumbers >= totalPageCount) {
        return range(1, totalPageCount);
      }

      const leftSiblingIndex = Math.max(current - siblingCount, 1);
      const rightSiblingIndex = Math.min(
        current + siblingCount,
        totalPageCount,
      );

      const shouldShowLeftDots = leftSiblingIndex > 2;
      const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

      const firstPageIndex = 1;
      const lastPageIndex = totalPageCount;

      if (!shouldShowLeftDots && shouldShowRightDots) {
        const leftItemCount = 3 + 2 * siblingCount;
        const leftRange = range(1, leftItemCount);

        return [...leftRange, DOTS, totalPageCount];
      }

      if (shouldShowLeftDots && !shouldShowRightDots) {
        const rightItemCount = 3 + 2 * siblingCount;
        const rightRange = range(
          totalPageCount - rightItemCount + 1,
          totalPageCount,
        );
        return [firstPageIndex, DOTS, ...rightRange];
      }

      if (shouldShowLeftDots && shouldShowRightDots) {
        const middleRange = range(leftSiblingIndex, rightSiblingIndex);
        return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
      }
    }, [total, pageSize, siblingCount, current]);

    if (paginationRange) {
      return paginationRange;
    }
    return [];
  };

  const paginationRange = usePagination();

  if (current === 0 || paginationRange.length < 2) {
    return null;
  }

  const lastPage = paginationRange[paginationRange.length - 1];
  const firstPage = paginationRange[0];

  const handlePageChange = (pageNumber: number) => {
    onChange(pageNumber);
  };

  const onNext = () => {
    handlePageChange(current + 1);
  };

  const onPrevious = () => {
    handlePageChange(current - 1);
  };

  const onFirst = () => {
    handlePageChange(firstPage);
  };

  const onLast = () => {
    handlePageChange(lastPage);
  };

  return (
    <StyledListContainer>
      <StyledChevronItem
        disabled={current === firstPage}
        onClick={onFirst}
        id="first-button"
      >
        <div className="skip">
          <IconManager
            size={IconSizeEnum.SMALL}
            color={
              current === firstPage ? theme.colors.grey_3 : theme.colors.black
            }
          >
            <SkipBackIcon />
          </IconManager>
        </div>
      </StyledChevronItem>
      <StyledChevronItem
        disabled={current === firstPage}
        onClick={onPrevious}
        id="prev-button"
      >
        <IconManager
          size={IconSizeEnum.SMALL}
          color={
            current === firstPage ? theme.colors.grey_3 : theme.colors.black
          }
        >
          <ChevronLeftIcon />
        </IconManager>
      </StyledChevronItem>

      {paginationRange.map((pageNumber, index) => {
        if (pageNumber === DOTS) {
          return (
            <StyledNumberPageItem
              $isCurrentPageNumber={false}
              key={"page-" + index}
              $disabled={true}
            >
              <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD}>{"..."}</BodyTyp>
            </StyledNumberPageItem>
          );
        }
        return (
          <StyledNumberPageItem
            key={"page-" + index}
            $isCurrentPageNumber={pageNumber === current}
            onClick={() => handlePageChange(pageNumber)}
            $disabled={false}
          >
            <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD}>{pageNumber}</BodyTyp>
          </StyledNumberPageItem>
        );
      })}
      <StyledChevronItem
        id="next-button"
        disabled={current === lastPage}
        onClick={onNext}
      >
        <IconManager
          color={
            current === lastPage ? theme.colors.grey_3 : theme.colors.black
          }
          size={IconSizeEnum.SMALL}
        >
          <ChevronRightIcon />
        </IconManager>
      </StyledChevronItem>
      <StyledChevronItem
        disabled={current === lastPage}
        onClick={onLast}
        id="last-button"
      >
        <div className="skip">
          <IconManager
            size={IconSizeEnum.SMALL}
            color={
              current === lastPage ? theme.colors.grey_3 : theme.colors.black
            }
          >
            <SkipIcon />
          </IconManager>
        </div>
      </StyledChevronItem>
    </StyledListContainer>
  );
};
